import React, { ReactElement, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  useTheme,
  Box,
  Menu,
  MenuItem,
  Stack,
  ListItemText,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import LogoutButton from './LogoutButton';
import Lang from './Lang';
import { useTranslation } from 'react-i18next';
import { AccountCircle, ExpandLess, ExpandMore, Home } from '@mui/icons-material';
import { useLogged } from '../context/useLogged';
import logo_gksd from './../assets/logo_gksd.svg';
import { customPalette } from '../theme';

export interface Link {
  name: string;
  path: string;
}

export const productLinks: Link[] = [
  { name: 'cluster.title', path: '/clusters' },
  { name: 'product.title', path: '/products' },
];

export const supplierLinks: Link[] = [
  { name: 'dashboard.title', path: '/clusters' },
  { name: 'retries.title', path: '/suppliers' },
];

const Navbar = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { loggedUser } = useLogged();
  const navigate = useNavigate();

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <AppBar position="static" sx={{ backgroundColor: theme.palette.background.default }}>
      <Toolbar disableGutters sx={{ paddingY: 2, paddingX: 5 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          <Box
            overflow={'hidden'}
            component="img"
            src={logo_gksd}
            alt="GKSD ProCure Logo"
            sx={{ height: 50, ml: 5, cursor: 'pointer', transform: 'scale(1.8)' }}
            onClick={() => navigate('/')}
          />
        </Typography>
        <Button variant="goldHover" component={Link} to="/">
          <Typography fontSize={'28px'}>
            <Home fontSize="inherit" />
          </Typography>
        </Button>
        {/* <Button variant="goldHover" component={Link} to="/clusters">
          <Typography variant="h2">{t('cluster.title')}</Typography>
        </Button>
        <Button variant="goldHover" component={Link} to="/products">
          <Typography variant="h2">{t('product.title')}</Typography>
        </Button> */}
        <CustomButtonLink title={t('product.management')} links={productLinks} />
        {loggedUser?.role === 'ADMIN' && (
          <Button variant="goldHover" component={Link} to="/users">
            <Typography variant="h2">{t('user.title')}</Typography>
          </Button>
        )}
        <Lang
          variant="outlined"
          sx={{
            mr: 2,
            backgroundColor: theme.palette.background.default,
            height: '52px',
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                border: 'none', // rimuove il bordo
              },
            },
          }}
          value={i18n.language}
          onChange={(e) => {
            i18n.changeLanguage(e.target.value);
          }}
        />
        <Button
          variant="goldHover"
          aria-label="account of current user"
          aria-controls="menu-appbar"
          aria-haspopup="true"
          onClick={handleMenu}
        >
          <Typography fontSize={'28px'}>
            <AccountCircle fontSize="inherit" />
          </Typography>
        </Button>
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem>
            <Stack flexDirection={'column'} alignItems={'center'}>
              <strong>{t('user.welcome')}</strong>
              <span> {loggedUser?.email} </span>
            </Stack>
          </MenuItem>
          <MenuItem>
            <Stack alignItems={'center'} width={'100%'}>
              <span>
                <LogoutButton />
              </span>
            </Stack>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;

interface CustomButtonLinkProps {
  title: string;
  links: Link[];
}
export const CustomButtonLink = ({ title, links }: CustomButtonLinkProps): ReactElement => {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [isFocus, setIsFocus] = useState<boolean>(false);

  const open = Boolean(anchorEl);

  const handleOpenClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClick = () => {
    if (links.length > 0) {
      navigate(links[0].path);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <Stack
        direction="row"
        sx={{
          textDecoration: isFocus || open ? 'underline' : 'none',
          transition: 'color 0.3s ease',
          cursor: 'pointer',
          color: open ? 'rgb(138 114 75)' : customPalette.gray900, // colore testo iniziale
          '&:hover': {
            color: 'rgb(138 114 75)', // oro al hover
            backgroundColor: 'inherit',
            textDecoration: 'underline',
            '& .hover-text, & svg': {
              color: 'rgb(138 114 75)', // oro anche su testo e icona
            },
          },
          '& svg': {
            color: 'black', // freccetta nera quando non hover
            transition: 'color 0.3s ease',
          },
        }}
        onClick={handleOpenClick}
      >
        <Typography
          className="hover-text"
          fontSize="23px"
          variant="h2"
          sx={{
            color: open ? 'rgb(138 114 75)' : customPalette.gray900,
            transition: 'color 0.3s ease',
          }}
        >
          {title}
        </Typography>

        {links.length > 1 && (
          <Stack>
            {open ? <ExpandLess sx={{ color: 'rgb(138 114 75) !important' }} /> : <ExpandMore />}
          </Stack>
        )}
      </Stack>
      <Menu
        id={title}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        keepMounted
        PaperProps={{
          sx: {
            borderRadius: 2, // arrotondato
            minWidth: 180, // larghezza minima
            boxShadow: '0px 4px 20px rgba(0,0,0,0.1)', // ombra leggera
            py: 1, // padding verticale del Paper
          },
        }}
      >
        {links.map((el) => (
          <MenuItem
            key={el.name}
            onClick={() => {
              setIsFocus(false);
              navigate(el.path);
              handleClose();
            }}
            sx={{
              transition: 'all 0.2s ease',
              borderRadius: 1,
              px: 2, // padding orizzontale
              '&:hover': {
                backgroundColor: 'rgba(138, 114, 75, 0.1)', // oro leggero di hover
                color: 'rgb(138 114 75)',
              },
            }}
          >
            <ListItemText>
              <Typography variant="h6">{t(el.name)}</Typography>
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};
